import type { IDBPDatabase, IDBPTransaction } from 'idb';
import {
  validId, validRevision, validLocalDate, validPlannedSession, validRoutineRevision,
  validExecutedSession, reprogramPlannedSession, omitPlannedSession, trainingFailure as failure,
} from '../domain/training.ts';
import type { PlannedSession, TrainingResult } from '../domain/training.ts';
import type {
  TrainingRepository, TrainingContext, PlanSessionCommand, ReprogramSessionRepositoryCommand,
  OmitSessionCommand, ReprogramSessionCommitResult, RevisionedTrainingCommitResult, PlannedSessionView,
} from '../ports/training-repository.ts';
import { openFenixDatabase, storageFailure, validDatasetControl } from './fenix-db.ts';
import type { FenixSchema } from './fenix-db.ts';
import { canonical } from './canonical.ts';
import { validMutation, validTrainingReceipt } from './training-receipts.ts';
import type { StoredTrainingReceipt } from './training-receipts.ts';
import { TrainingLabFaults } from './training-idb.ts';
import type { TrainingFault } from './training-idb.ts';

const stores = ['control', 'training_planned_sessions', 'training_planned_session_history',
  'training_routine_revisions', 'training_receipts', 'training_executions'] as const;
type Tx = IDBPTransaction<FenixSchema, typeof stores, 'readonly' | 'readwrite'>;
type Port = Pick<TrainingRepository, 'readPlannedSession' | 'readPlannedSessionByDate' |
  'readPlannedSessionRevision' | 'planSession' | 'reprogramSession' | 'omitSession'>;
type Command = PlanSessionCommand | ReprogramSessionRepositoryCommand | OmitSessionCommand;
const scope = (datasetId: string) => IDBKeyRange.bound([datasetId], [datasetId, []]);
function inject(fault: TrainingFault | null, point: TrainingFault) {
  if (fault === point) throw new DOMException('Synthetic planning abort', 'AbortError');
}

export class IndexedTrainingPlanning implements Port {
  private available = true;
  constructor(private readonly db: IDBPDatabase<FenixSchema>, private readonly faults = new TrainingLabFaults()) {
    db.addEventListener('versionchange', () => this.close());
    db.addEventListener('close', () => { this.available = false; });
  }
  close() { this.available = false; this.db.close(); }
  private async transaction<M extends 'readonly' | 'readwrite', T>(mode: M, run: (tx: IDBPTransaction<FenixSchema, typeof stores, M>) => Promise<TrainingResult<T>>): Promise<TrainingResult<T>> {
    if (!this.available) return failure('STORAGE_UNAVAILABLE');
    let tx: IDBPTransaction<FenixSchema, typeof stores, M> | undefined;
    try {
      tx = this.db.transaction(stores, mode, { durability: 'strict' });
      void tx.done.catch(() => {});
      const result = await run(tx);
      if (!result.ok) { tx.abort(); await tx.done.catch(() => {}); return result; }
      await tx.done;
      return result;
    } catch (error) {
      if (tx) { try { tx.abort(); } catch { /* settled */ } await tx.done.catch(() => {}); }
      return storageFailure(error);
    }
  }
  private async control(tx: Tx, context?: TrainingContext) {
    const c = await tx.objectStore('control').get('control');
    if (!validDatasetControl(c)) return failure('CORRUPTION_DETECTED');
    if (c.restoreFence !== null) return failure('RESTORE_IN_PROGRESS');
    if (c.recoveryDatasetId !== null) return failure('CORRUPTION_DETECTED');
    if (context && (c.activeDatasetId !== context.datasetId || c.controlEpoch !== context.controlEpoch)) return failure('DATASET_STALE');
    return { ok: true as const, value: c };
  }
  private async historical(tx: Tx, datasetId: string, sessionId: string, revision: number): Promise<TrainingResult<PlannedSession>> {
    const row = await tx.objectStore('training_planned_session_history').get([datasetId, sessionId, revision]);
    if (!row) return failure('NOT_FOUND');
    if (row.datasetId !== datasetId || !validPlannedSession(row.record)
      || row.record.sessionId !== sessionId || row.record.revision !== revision) return failure('CORRUPTION_DETECTED');
    return { ok: true, value: row.record };
  }
  // The date claim IS the current record. All planning writers scan and write in
  // one readwrite transaction on this store. IDB serializes overlapping writers,
  // including different connections. Omission never removes the current record.
  // Linear scan is intentional for v2: no new index, store or physical upgrade.
  private async current(tx: Tx, datasetId: string): Promise<TrainingResult<PlannedSession[]>> {
    const sessions: PlannedSession[] = [];
    const dates = new Set<string>();
    let cursor = await tx.objectStore('training_planned_sessions').openCursor(scope(datasetId));
    while (cursor) {
      const row = cursor.value;
      if (row.datasetId !== datasetId || !validPlannedSession(row.record)
        || cursor.key[1] !== row.record.sessionId || dates.has(row.record.plannedLocalDate)) return failure('CORRUPTION_DETECTED');
      const history = await this.historical(tx, datasetId, row.record.sessionId, row.record.revision);
      if (!history.ok || canonical(history.value) !== canonical(row.record)) return failure('CORRUPTION_DETECTED');
      dates.add(row.record.plannedLocalDate); sessions.push(row.record);
      cursor = await cursor.continue();
    }
    return { ok: true, value: sessions };
  }
  private query(id?: string, date?: string) {
    return this.transaction('readonly', async tx => {
      const c = await this.control(tx); if (!c.ok) return c;
      const all = await this.current(tx, c.value.activeDatasetId); if (!all.ok) return all;
      const session = all.value.find(s => id ? s.sessionId === id : s.plannedLocalDate === date);
      return { ok: true as const, value: session ? { session, context: {
        datasetId: c.value.activeDatasetId, controlEpoch: c.value.controlEpoch,
      } } : null };
    });
  }
  async readPlannedSession(id: string): Promise<TrainingResult<PlannedSessionView>> {
    if (!validId(id)) return failure('VALIDATION_ERROR');
    const r = await this.query(id);
    if (!r.ok) return r;
    return r.value ? { ok: true, value: r.value } : failure('NOT_FOUND');
  }
  readPlannedSessionByDate(date: string) {
    return validLocalDate(date) ? this.query(undefined, date) : Promise.resolve(failure('VALIDATION_ERROR'));
  }
  readPlannedSessionRevision(id: string, revision: number) {
    if (!validId(id) || !validRevision(revision)) return Promise.resolve(failure('VALIDATION_ERROR'));
    return this.transaction('readonly', async tx => {
      const c = await this.control(tx); if (!c.ok) return c;
      return this.historical(tx, c.value.activeDatasetId, id, revision);
    });
  }
  private async mutate(action: 'plan' | 'reprogram' | 'omit', input: Command): Promise<TrainingResult<ReprogramSessionCommitResult>> {
    if (!validMutation(input)) return failure('VALIDATION_ERROR');
    let command: Command;
    let fingerprint: string;
    try { command = structuredClone(input); fingerprint = canonical({ kind: 'planning', action, command }); }
    catch { return failure('VALIDATION_ERROR'); }
    const id = 'session' in command ? command.session?.sessionId : command.sessionId;
    if (!validId(id) || (action === 'plan'
      ? !('session' in command) || !validPlannedSession(command.session)
      : !('expectedRevision' in command) || !validRevision(command.expectedRevision))
      || (action === 'reprogram' && (!('targetLocalDate' in command) || !validLocalDate(command.targetLocalDate)))) return failure('VALIDATION_ERROR');
    let lostAck = false;
    const result = await this.transaction<'readwrite', ReprogramSessionCommitResult>('readwrite', async tx => {
      const checked = await this.control(tx, command.context); if (!checked.ok) return checked;
      const control = checked.value, datasetId = control.activeDatasetId;
      const prior = await tx.objectStore('training_receipts').get([datasetId, command.operationId]);
      if (prior) {
        if (prior.datasetId !== datasetId || !validTrainingReceipt(prior.record)
          || prior.record.datasetId !== datasetId || prior.record.operationId !== command.operationId) return failure('CORRUPTION_DETECTED');
        const record = prior.record;
        if (record.fingerprint !== fingerprint) return failure('INVARIANT_CONFLICT');
        if (record.kind !== 'planning' || record.action !== action || record.receipt.ownerId !== id
          || record.revisionRef !== id || !validRevision(record.receipt.revision)) return failure('CORRUPTION_DETECTED');
        const historical = await this.historical(tx, datasetId, id, record.receipt.revision);
        if (!historical.ok) return failure('CORRUPTION_DETECTED');
        return { ok: true, value: { status: 'REPLAYED', receipt: { ...record.receipt, revision: record.receipt.revision }, effect: record.effect ?? 'CHANGED' } };
      }
      const all = await this.current(tx, datasetId); if (!all.ok) return all;
      const current = all.value.find(s => s.sessionId === id);
      let next: PlannedSession;
      let effect: 'NOOP' | 'CHANGED' = 'CHANGED';
      if (action === 'plan' && 'session' in command) {
        next = command.session;
        if (current || next.revision !== 1 || next.explicitOmission || next.cohortLocalDate !== next.plannedLocalDate
          || all.value.some(s => s.plannedLocalDate === next.plannedLocalDate)) return failure('INVARIANT_CONFLICT');
        const routine = await tx.objectStore('training_routine_revisions').get([datasetId, next.routineRevisionRef]);
        if (!routine) return failure('NOT_FOUND');
        if (routine.datasetId !== datasetId || !validRoutineRevision(routine.record)
          || routine.record.routineRevisionId !== next.routineRevisionRef) return failure('CORRUPTION_DETECTED');
      } else {
        if (!current) return failure('NOT_FOUND');
        // Guard existing execution facts only; this block has no execution writer.
        let cursor = await tx.objectStore('training_executions').openCursor(scope(datasetId));
        while (cursor) {
          const row = cursor.value;
          if (row.datasetId !== datasetId || !validExecutedSession(row.record) || cursor.key[1] !== row.record.executionId) return failure('CORRUPTION_DETECTED');
          if (row.record.plannedSessionId === id) return failure('INVARIANT_CONFLICT');
          cursor = await cursor.continue();
        }
        if (action === 'reprogram' && 'targetLocalDate' in command) {
          const changed = reprogramPlannedSession(current, command, all.value.some(s => s.plannedLocalDate === command.targetLocalDate));
          if (!changed.ok) return changed;
          next = changed.value.session; effect = changed.value.status;
        } else if (action === 'omit' && 'expectedRevision' in command) {
          const omitted = omitPlannedSession(current, command); if (!omitted.ok) return omitted;
          next = omitted.value;
        } else return failure('VALIDATION_ERROR');
      }
      if (effect === 'CHANGED' && !Number.isSafeInteger(control.datasetRevision + 1)) return failure('INVARIANT_CONFLICT');
      const fault = this.faults.take();
      if (effect === 'CHANGED') {
        // One replacement releases A and claims B simultaneously; no intermediate
        // unoccupied state exists, even inside the transaction.
        await tx.objectStore('training_planned_sessions').put({ datasetId, record: next }, [datasetId, id]);
        inject(fault, 'after-current');
        await tx.objectStore('training_planned_session_history').add({ datasetId, record: next }, [datasetId, id, next.revision]);
        inject(fault, 'after-revision');
      }
      const receipt = { operationId: command.operationId, ownerId: id, revision: next.revision };
      const record: StoredTrainingReceipt = { operationId: command.operationId, datasetId, kind: 'planning', action,
        fingerprint, revisionRef: id, receipt, ...(action === 'reprogram' ? { effect } : {}) };
      await tx.objectStore('training_receipts').add({ datasetId, record }, [datasetId, command.operationId]);
      inject(fault, 'after-receipt');
      // NOOP receipts bind the intent but do not manufacture domain revisions.
      if (effect === 'CHANGED') await tx.objectStore('control').put({ ...control, datasetRevision: control.datasetRevision + 1 });
      inject(fault, 'after-control');
      lostAck = fault === 'lost-ack';
      return { ok: true, value: { status: 'APPLIED', receipt, effect } };
    });
    return result.ok && lostAck ? failure('STORAGE_UNAVAILABLE') : result;
  }
  private async revisioned(result: Promise<TrainingResult<ReprogramSessionCommitResult>>): Promise<TrainingResult<RevisionedTrainingCommitResult>> {
    const r = await result;
    return r.ok ? { ok: true, value: { status: r.value.status, receipt: r.value.receipt } } : r;
  }
  planSession(command: PlanSessionCommand) { return this.revisioned(this.mutate('plan', command)); }
  reprogramSession(command: ReprogramSessionRepositoryCommand) { return this.mutate('reprogram', command); }
  omitSession(command: OmitSessionCommand) { return this.revisioned(this.mutate('omit', command)); }
}
export async function openTrainingPlanning(name: string, faults = new TrainingLabFaults()): Promise<TrainingResult<IndexedTrainingPlanning>> {
  const opened = await openFenixDatabase(name);
  return opened.ok ? { ok: true, value: new IndexedTrainingPlanning(opened.value, faults) } : opened;
}
